import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Button } from 'flowbite-react';
import { api } from '../api';
import { getEmploymentStatusLabel, getIncomeSourceLabel } from '../utils/translations';
import { Document, Detail } from '../types/user';
import { UserLoan } from '../types/loan';

const UserDetail: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const [userDetail, setUserDetail] = useState<Detail | null>(null);
  const [userLoans, setUserLoans] = useState<UserLoan[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const data = await api.users.getById(userId!);
        setUserDetail(data.user);
        setUserLoans(data.loans);
        setDocuments(data.documents);
      } catch (error) {
        console.error('Error fetching user details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserDetails();
  }, [userId]);

  const handleDocumentDownload = (documentId: number) => {
    try {
      const url = api.documents.getDownloadUrl(documentId);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error opening document:', error);
      alert('Failed to open document');
    }
  };

  const formatCurrency = (amount: number | undefined) => {
    return amount ? `$${amount.toLocaleString()}` : 'N/A';
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!userDetail) {
    return <div>User not found</div>;
  }

  return (
    <div className="max-w-5xl mx-auto p-8">
      <Card className="shadow-xl">
        <div className="border-b border-gray-200 pb-6 mb-8">
          <h5 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white mb-2">
            {userDetail.first_name || 'User'}'s Profile
          </h5>
          <p className="text-gray-500">
            Member since {userDetail.created_at ? new Date(userDetail.created_at).toLocaleDateString() : 'N/A'}
          </p>
        </div>

        <div className="space-y-8">
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-8">
            <h6 className="text-lg font-semibold mb-4 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Personal Information
            </h6>
            <div className="space-y-3">
              {[
                { label: 'Email', value: userDetail.email || 'N/A' },
                { label: 'Mobile', value: userDetail.mobile_number || 'N/A' },
                { label: 'Age', value: userDetail.age || 'N/A' },
                { label: 'Gender', value: userDetail.gender || 'N/A' },
              ].map(({ label, value }) => (
                <div key={label} className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-gray-400">{label}</span>
                  <span className="font-medium">{value}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-8">
            <h6 className="text-lg font-semibold mb-4 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Financial Information
            </h6>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600 dark:text-gray-400">Employment Status</span>
                <span className="font-medium text-right">
                  {getEmploymentStatusLabel(userDetail.employment_status)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 dark:text-gray-400">Monthly Income</span>
                <span className="font-medium">
                  {(userDetail.average_monthly_income)}
                </span>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400 block mb-3">Income Sources</span>
                <div className="flex flex-wrap gap-3">
                  {(userDetail.income_sources || []).length > 0 ? (
                    userDetail.income_sources.map((source) => (
                      <span key={source} className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-100 rounded-full text-sm">
                        {getIncomeSourceLabel(source)}
                      </span>
                    ))
                  ) : (
                    <span className="text-gray-500">No income sources listed</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-8">
            <h6 className="text-lg font-semibold mb-4 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              Loans
            </h6>
            {userLoans && userLoans.length > 0 ? (
              <div className="space-y-4">
                {userLoans.map((loan) => (
                  <div key={loan.id} className="p-6 bg-white dark:bg-gray-700 rounded-lg">
                    <div className="flex justify-between items-center mb-3">
                      <span className="font-medium">
                        SAR {loan.borrow_amount.toLocaleString()}
                      </span>
                    </div>
                    
                    <div className="space-y-3">
                      <div>
                        <span className="text-sm text-gray-500">Purposes</span>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {loan.loan_purposes.map((purpose) => (
                            <span key={purpose} 
                              className="px-2 py-1 bg-purple-50 dark:bg-purple-900 
                                       text-purple-700 dark:text-purple-100 rounded-md text-xs">
                              {purpose}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      <div>
                        <span className="text-sm text-gray-500">Application Sources</span>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {loan.application_sources.map((source) => (
                            <span key={source} 
                              className="px-2 py-1 bg-gray-50 dark:bg-gray-600 
                                       text-gray-700 dark:text-gray-100 rounded-md text-xs">
                              {source}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <svg className="w-12 h-12 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-gray-500">No loan applications found</p>
                <p className="text-sm text-gray-400 mt-1">This user hasn't applied for any loans yet</p>
              </div>
            )}
          </div>

          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-8">
            <h6 className="text-lg font-semibold mb-4 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Documents
            </h6>
            {documents && documents.length > 0 ? (
              <div className="space-y-4">
                {documents.map((doc) => (
                  <div key={doc.document_id} className="p-4 bg-white dark:bg-gray-700 rounded-lg">
                    <div className="flex justify-between items-start">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{doc.file_name}</span>
                          <span className="inline-flex items-center rounded-full bg-blue-100 dark:bg-blue-900 px-2.5 py-0.5 text-xs font-medium text-blue-800 dark:text-blue-100">
                            {doc.document_type}
                          </span>
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          Uploaded: {new Date(doc.created_at).toLocaleString()}
                        </div>
                      </div>
                      <Button
                        size="xs"
                        gradientDuoTone="purpleToBlue"
                        onClick={() => handleDocumentDownload(doc.document_id)}
                      >
                        Download
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <svg className="w-12 h-12 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-gray-500">No documents uploaded</p>
                <p className="text-sm text-gray-400 mt-1">No verification documents have been submitted</p>
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default UserDetail; 